import React, { useEffect, useState } from 'react';
import { Save, Building2, Palette, CreditCard, ShieldCheck } from 'lucide-react';
import clientService from '../../services/clientService';

const defaultSettings = {
  agency: {
    name: 'Karni Digital Solutions',
    email: '',
    phone: '',
    address: ''
  },
  branding: {
    primary: '#003366',
    accent: '#FFD700',
    logoUrl: ''
  },
  payments: {
    currency: 'THB',
    currencySymbol: '฿',
    defaultMethod: 'bank_transfer'
  },
  portal: {
    enabled: true
  }
};

const Settings = () => {
  const [settings, setSettings] = useState(defaultSettings);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const apiSettings = await clientService.getAdminSettings();
        if (apiSettings && Object.keys(apiSettings).length) {
          setSettings({ ...defaultSettings, ...apiSettings });
          return;
        }
        // fallback to localStorage if API empty
        const raw = localStorage.getItem('admin_settings');
        if (raw) setSettings({ ...defaultSettings, ...JSON.parse(raw) });
      } catch (e) {
        // fallback to localStorage on error
        const raw = localStorage.getItem('admin_settings');
        if (raw) setSettings({ ...defaultSettings, ...JSON.parse(raw) });
      }
    };
    load();
  }, []);

  const save = () => {
    const doSave = async () => {
      try {
        setSaving(true);
        await clientService.updateAdminSettings(settings);
        localStorage.setItem('admin_settings', JSON.stringify(settings));
        setSaving(false);
        alert('Settings saved');
      } catch (e) {
        setSaving(false);
        alert('Failed to save settings');
      }
    };
    doSave();
  };

  const onChange = (scope, key, value) => {
    setSettings(prev => ({
      ...prev,
      [scope]: {
        ...prev[scope],
        [key]: value
      }
    }));
  };

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-primary mb-2">Settings</h1>
        <p className="text-gray-600">Manage agency, branding, payments and client portal</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: forms */}
        <div className="lg:col-span-2 space-y-6">
          {/* Agency */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2"><Building2 className="w-5 h-5"/> Agency Info</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Agency Name</label>
                <input value={settings.agency.name} onChange={(e)=>onChange('agency','name',e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary"/>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Email</label>
                <input type="email" value={settings.agency.email} onChange={(e)=>onChange('agency','email',e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary"/>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Phone</label>
                <input value={settings.agency.phone} onChange={(e)=>onChange('agency','phone',e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary"/>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Address</label>
                <textarea rows="3" value={settings.agency.address} onChange={(e)=>onChange('agency','address',e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary"/>
              </div>
            </div>
          </div>

          {/* Branding */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2"><Palette className="w-5 h-5"/> Branding</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Primary Color</label>
                <input type="color" value={settings.branding.primary} onChange={(e)=>onChange('branding','primary',e.target.value)} className="h-10 w-16 p-0 border-2 border-gray-200 rounded"/>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Accent Color</label>
                <input type="color" value={settings.branding.accent} onChange={(e)=>onChange('branding','accent',e.target.value)} className="h-10 w-16 p-0 border-2 border-gray-200 rounded"/>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-bold text-gray-700 mb-2">Logo URL</label>
                <input value={settings.branding.logoUrl} onChange={(e)=>onChange('branding','logoUrl',e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary"/>
              </div>
            </div>
          </div>

          {/* Payments */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2"><CreditCard className="w-5 h-5"/> Payments</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Currency</label>
                <select value={settings.payments.currency} onChange={(e)=>onChange('payments','currency',e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary">
                  {['THB','USD','INR','EUR'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Symbol</label>
                <input value={settings.payments.currencySymbol} onChange={(e)=>onChange('payments','currencySymbol',e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary"/>
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">Default Method</label>
                <select value={settings.payments.defaultMethod} onChange={(e)=>onChange('payments','defaultMethod',e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-primary">
                  {['bank_transfer','cash','upi','card','cheque','other'].map(m => <option key={m} value={m}>{m.replace('_',' ')}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Portal */}
          <div className="bg-white rounded-xl shadow-md p-6">
            <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2"><ShieldCheck className="w-5 h-5"/> Client Portal</h2>
            <label className="flex items-center gap-3">
              <input type="checkbox" checked={settings.portal.enabled} onChange={(e)=>onChange('portal','enabled',e.target.checked)} />
              <span>Enable client portal (will require setup of client accounts)</span>
            </label>
          </div>
        </div>

        {/* Right: actions */}
        <div className="space-y-6">
          <div className="bg-white rounded-xl shadow-md p-6">
            <h3 className="text-lg font-bold mb-4">Actions</h3>
            <button onClick={save} disabled={saving} className="w-full bg-primary hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
              <Save className="w-5 h-5"/>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
            <button onClick={()=>{localStorage.removeItem('admin_settings'); setSettings(defaultSettings);}} className="w-full mt-3 bg-gray-200 hover:bg-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold">Reset to Default</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
